<template>
  <div class="time-machine-control" :class="{ disabled: disabled }">
    <template v-if="!isSegmentOpen">
      <div class="time-machine-control__wrapper">
        <div class="time-machine-control__header">
          <div class="time-machine-control__header__left">
            <div v-if="!disabled" class="time-machine-control__header__left__item">
              <div class="date">{{ $t('reporting.timeMachine.from', { date: startDateStr }) }}</div>
              <div class="time">
                <i class="icon icon--time-machine-timer"></i>
                {{ duration }}{{ $t('reporting.timeMachine.minutes') }}
              </div>
            </div>
            <div v-if="!disabled" class="time-machine-control__header__left__item">
              <div class="play-time">{{ formattedCurrentTime }}</div>
            </div>
            <div class="time-machine-control__header__left__item">
              <div class="segment-filter" @click="isSegmentOpen = true">
                <span class="segment-filter__text">{{ $t('reporting.timeMachine.discoveries.segments') }}</span>
                <i class="icon icon--segment-filter"></i>
              </div>
            </div>
          </div>
          <div v-if="!disabled" class="time-machine-control__header__right">
            <div class="label"></div>
            <div class="label-text">PV</div>
          </div>
        </div>
        <div class="time-machine-control__content">
          <div class="time-machine-control__content__left">
            <div class="time-machine-control__content__left__speed" @click="toggleSpeedDropdown">
              <span class="speed-value">{{ speed }}x</span>
              <i class="icon icon--time-machine-chevron"></i>
              <transition name="slide">
                <ul v-show="isSpeedDropdownOpen" class="speed-dropdown">
                  <li v-for="opt in speedOptions" :key="opt"
                      :class="{ active: opt === speed }"
                      @click.stop="selectSpeed(opt)">{{ opt }}x</li>
                </ul>
              </transition>
            </div>
            <div class="time-machine-control__content__left__play">
              <button type="button" class="play-btn" :class="{ playing: isPlaying }" @click="togglePlay">
                <span>Play</span>
                <i class="icon icon--time-machine-play" :class="{ 'icon--time-machine-pause': isPlaying }"></i>
              </button>
            </div>
          </div>
          <div class="time-machine-control__content__timeline" ref="timeline">
            <div class="time-machine-control__content__timeline__chart" ref="chart"
                 @mousedown.prevent="onChartMouseDown">
              <svg :width="svgTotalWidth" :height="chartHeight" class="bar-chart" shape-rendering="crispEdges">
                <rect v-for="(bar, idx) in barPositions" :key="idx"
                      :x="bar.x"
                      :y="bar.y"
                      :width="barWidth"
                      :height="bar.height"
                      :fill="getBarFill(bar)"
                />
              </svg>
            </div>
            <div class="playhead"
                 :class="{ dragging: dragType !== null }"
                 :style="{ left: `${playheadLeftPx}px` }"
                 @mousedown.prevent.stop="onPlayheadMouseDown">
              <i class="icon icon--time-machine-playhead" :class="{ active: dragType !== null }"></i>
            </div>
            <div class="time-machine-control__content__timeline__nav"
                 :class="{ dragging: dragType !== null }"
                 :style="{ left: `${playheadLeftPx}px` }"
                 @mousedown.prevent.stop="onNavMouseDown">
              <i class="icon icon--time-machine-control nav-icon--left" :class="{ active: dragType !== null }"></i>
              <i class="icon icon--time-machine-control nav-icon--right" :class="{ active: dragType !== null }"></i>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="time-machine-control__segment">
        <div class="time-machine-control__segment__header">
          <span class="segment-title">{{ $t('reporting.timeMachine.segmentSetting') }}</span>
          <button type="button" class="segment-close" @click="isSegmentOpen = false">
            <i class="icon icon--close-size20"></i>
          </button>
        </div>
        <div class="time-machine-control__segment__body">
          <div class="segment-device">
            <template v-for="item in deviceList">
              <button type="button" :key="item" class="device-btn" :class="{ on: segmentDevice === item }"
                      @click="selectDevice(item)">
                <i class="icon" :class="`icon--device-${item}`"></i>
              </button>
              <template v-if="item !== deviceList[deviceList.length - 1]">
                <div class="device-line" :key="`line-${item}`"></div>
              </template>
            </template>
          </div>
          <div class="segment-referrer">
            <div class="referrer-select" @click.stop="isReferrerOpen = !isReferrerOpen">
              <span class="referrer-select__text">{{ segmentReferrer === 'all' ? 'All' : segmentReferrer }}</span>
              <i class="icon icon--drop-down"></i>
            </div>
            <transition name="slide">
              <ul v-show="isReferrerOpen" class="referrer-dropdown">
                <li :class="{ on: segmentReferrer === 'all' }" @click="selectReferrer('all')">All</li>
                <li v-for="item in referrerList" :key="item.refer"
                    :class="{ on: segmentReferrer === item.refer }"
                    :title="item.refer"
                    @click="selectReferrer(item.refer)">
                  <span class="referrer-name">{{ item.refer }}</span>
                  <span class="referrer-percent">({{ item.percent }}%)</span>
                </li>
              </ul>
            </transition>
          </div>
          <div class="segment-visitor">
            <span class="checkbox_wrap" :class="{ on: segmentNew }" @click="toggleNew">
              <i class="icon icon--chkbox"></i>
              <span class="text">{{ $t('heatmap.cvr_exit.new') }}</span>
            </span>
            <span class="checkbox_wrap" :class="{ on: segmentReturn }" @click="toggleReturn">
              <i class="icon icon--chkbox"></i>
              <span class="text">{{ $t('heatmap.cvr_exit.revisit') }}</span>
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { utcToLocalTimezone } from '@/libs/utils'
import { format } from 'date-fns'

export default {
  name: 'HeatmapTimeMachineControl',
  props: {
    startDate: {
      type: String,
      default: ''
    },
    startTime: {
      type: String,
      default: ''
    },
    duration: {
      type: Number,
      default: 5
    },
    pvData: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    timezoneName: {
      type: String,
      default: ''
    },
    reportStartTimestamp: {
      type: Number,
      default: 0
    },
    basicInfo: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      isPlaying: false,
      currentTime: 0,
      speed: 1,
      speedOptions: [1, 10, 60],
      viewportStart: 0,
      viewportSize: 60,
      dragType: null,
      dragStartX: 0,
      animationId: null,
      lastFrameTime: null,
      dragRafId: null,
      lastEmittedFrame: -1,
      isSpeedDropdownOpen: false,
      isSegmentOpen: false,
      segmentDevice: 'desktop',
      deviceList: ['desktop', 'tablet', 'phone'],
      segmentReferrer: 'all',
      isReferrerOpen: false,
      segmentNew: true,
      segmentReturn: true,
      chartWidth: 0,
      chartHeight: 50
    }
  },
  computed: {
    defaultDevice () {
      if (!this.basicInfo || !this.basicInfo.device) return null
      const devices = this.basicInfo.device
      let maxPv = -1
      let maxDevice = null
      for (const [key, val] of Object.entries(devices)) {
        if (val && val.pv > maxPv) {
          maxPv = val.pv
          maxDevice = key
        }
      }
      return maxDevice
    },
    referrerList () {
      if (!this.basicInfo || !this.basicInfo.device) return []
      const deviceData = this.basicInfo.device[this.segmentDevice]
      if (!deviceData || !deviceData.refer) return []
      const totalPv = deviceData.pv || 1
      return deviceData.refer.map(r => ({
        refer: r.refer,
        percent: ((r.pv / totalPv) * 100).toFixed(1)
      }))
    },
    totalSeconds () {
      return this.duration * 60
    },
    startTimestamp () {
      if (this.reportStartTimestamp) return this.reportStartTimestamp
      if (!this.pvData.length) return 0
      return this.pvData[0].timestamp
    },
    pxPerSecond () {
      return this.chartWidth / this.totalSeconds
    },
    formattedCurrentTime () {
      const mins = Math.floor(this.currentTime / 60)
      const secs = Math.floor(this.currentTime % 60)
      return String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0')
    },
    maxPv () {
      if (!this.pvData.length) return 1
      let max = 0
      for (let i = 0; i < this.pvData.length; i++) {
        const frame = this.pvData[i]
        const val = (frame.activePv || 0)
        if (val > max) max = val
      }
      return max || 1
    },
    barStep () {
      return this.pxPerSecond
    },
    barWidth () {
      const barWidthByDuration = {
        5: 1,
        10: 0.85,
        15: 0.57,
        20: 0.42,
        25: 0.34,
        30: 0.29
      }

      if (barWidthByDuration[this.duration]) {
        return barWidthByDuration[this.duration]
      }

      return 1
    },
    barOffset () {
      if (this.barWidth >= 1) return 0
      return (1 - this.barWidth) / 2
    },
    barPositions () {
      const bars = []
      if (!this.pvData.length || !this.chartWidth) return bars
      for (let i = 0; i < this.pvData.length; i++) {
        const frame = this.pvData[i]
        const pv = frame.activePv || 0
        if (!pv) continue
        const sec = frame.timestamp != null
          ? (frame.timestamp - this.startTimestamp) / 1000
          : i
        const height = Math.min((pv / this.maxPv) * this.chartHeight, this.chartHeight)
        bars.push({
          x: sec * this.barStep,
          y: this.chartHeight - height,
          height: height,
          timestamp: frame.timestamp != null ? frame.timestamp : this.startTimestamp + (sec * 1000),
          totalEventCount: frame.totalEventCount || 0
        })
      }
      return bars
    },
    currentPlaybackTimestamp () {
      return this.startTimestamp + (this.currentTime * 1000)
    },
    playheadLeftPx () {
      return this.currentTime * this.barStep
    },
    svgTotalWidth () {
      return this.chartWidth
    },
    startDateStr () {
      if (!this.startDate || !this.timezoneName) return ''
      const str = this.startDate.endsWith('Z') ? this.startDate : this.startDate + 'Z'
      const d = utcToLocalTimezone(new Date(str), this.timezoneName)
      if (!d || isNaN(d.getTime())) return ''

      return format(d, 'YYYY-MM-DD HH:mm')
    }
  },
  watch: {
    disabled (val) {
      if (val) {
        this.resetPlaybackState()
      }
    },
    pvData (val, oldVal) {
      if (val !== oldVal) {
        this.resetPlaybackState()
      }
    },
    defaultDevice: {
      handler (val) {
        if (val && this.segmentDevice !== val) {
          this.segmentDevice = val
          this.onSegmentChange()
        }
      },
      immediate: true
    }
  },
  mounted () {
    document.addEventListener('click', this.handleOutsideClick)
    this.updateChartWidth()
    window.addEventListener('resize', this.updateChartWidth)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.handleOutsideClick)
    window.removeEventListener('resize', this.updateChartWidth)
    document.removeEventListener('mousemove', this.onDragMove)
    document.removeEventListener('mouseup', this.onDragEnd)
    this.stopPlayback()
  },
  methods: {
    resetPlaybackState () {
      this.stopPlayback()
      this.isPlaying = false
      this.currentTime = 0
      this.lastEmittedFrame = -1
      this.viewportStart = 0
    },
    updateChartWidth () {
      this.$nextTick(() => {
        if (this.$refs.chart) {
          this.chartWidth = this.$refs.chart.clientWidth
        }
      })
    },
    xToTime (x) {
      return Math.max(0, Math.min(x / this.barStep, this.totalSeconds))
    },
    togglePlay () {
      if (this.disabled) return
      if (this.isPlaying) {
        this.pause()
      } else {
        this.play()
      }
    },
    play () {
      if (this.currentTime >= this.totalSeconds) {
        this.currentTime = 0
        this.viewportStart = 0
      }
      this.isPlaying = true
      const frameIdx = Math.floor(this.currentTime)
      this.lastEmittedFrame = frameIdx
      this.$emit('frame-change', frameIdx, this.speed)
      this.lastFrameTime = performance.now()
      this.animationId = requestAnimationFrame(this.animationLoop)
    },
    pause () {
      this.stopPlayback()
      this.isPlaying = false
    },
    stopPlayback () {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }
      this.lastFrameTime = null
    },
    animationLoop (timestamp) {
      if (!this.isPlaying) return
      if (this.lastFrameTime) {
        const delta = (timestamp - this.lastFrameTime) / 1000
        this.currentTime += delta * this.speed
        if (this.currentTime >= this.totalSeconds) {
          this.currentTime = this.totalSeconds
          this.pause()
          return
        }
        const frameIdx = Math.floor(this.currentTime)
        if (frameIdx !== this.lastEmittedFrame) {
          this.lastEmittedFrame = frameIdx
          this.$emit('frame-change', frameIdx, this.speed)
        }
        if (this.currentTime >= this.viewportStart + this.viewportSize) {
          this.viewportStart = Math.min(
            Math.floor(this.currentTime),
            this.totalSeconds - this.viewportSize
          )
        }
      }
      this.lastFrameTime = timestamp
      this.animationId = requestAnimationFrame(this.animationLoop)
    },
    toggleSpeedDropdown () {
      if (this.disabled) return
      this.isSpeedDropdownOpen = !this.isSpeedDropdownOpen
    },
    selectSpeed (value) {
      this.speed = value
      this.isSpeedDropdownOpen = false
    },
    onChartMouseDown (e) {
      if (this.disabled) return
      if (this.isPlaying) this.pause()
      const rect = this.$refs.chart.getBoundingClientRect()
      const x = e.clientX - rect.left + this.$refs.chart.scrollLeft
      this.currentTime = this.xToTime(x)
      this.dragType = 'playhead'
      this.dragStartX = e.clientX
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
    },
    onPlayheadMouseDown (e) {
      if (this.disabled) return
      if (this.isPlaying) this.pause()
      this.dragType = 'playhead'
      this.dragStartX = e.clientX
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
    },
    onNavMouseDown (e) {
      if (this.disabled) return
      if (this.isPlaying) this.pause()
      this.dragType = 'playhead'
      this.dragStartX = e.clientX
      document.addEventListener('mousemove', this.onDragMove)
      document.addEventListener('mouseup', this.onDragEnd)
    },
    onDragMove (e) {
      if (this.dragRafId) return
      this.dragRafId = requestAnimationFrame(() => {
        this.dragRafId = null
        if (!this.$refs.chart) return
        const rect = this.$refs.chart.getBoundingClientRect()

        if (this.dragType === 'playhead') {
          const x = e.clientX - rect.left + this.$refs.chart.scrollLeft
          this.currentTime = this.xToTime(x)
        }
      })
    },
    onDragEnd () {
      this.dragType = null
      if (this.dragRafId) {
        cancelAnimationFrame(this.dragRafId)
        this.dragRafId = null
      }
      document.removeEventListener('mousemove', this.onDragMove)
      document.removeEventListener('mouseup', this.onDragEnd)
    },
    handleOutsideClick (e) {
      const speedEl = this.$el.querySelector('.time-machine-control__content__left__speed')
      if (this.isSpeedDropdownOpen && speedEl && !speedEl.contains(e.target)) {
        this.isSpeedDropdownOpen = false
      }
      const referrerEl = this.$el.querySelector('.segment-referrer')
      if (this.isReferrerOpen && referrerEl && !referrerEl.contains(e.target)) {
        this.isReferrerOpen = false
      }
    },
    getBarFill (bar) {
      if (this.disabled) return '#D7D7D7'
      if (!this.isPlaying && this.currentTime <= 0) return '#FF8FA3'
      if (bar.timestamp > this.currentPlaybackTimestamp) return '#FFE2E7'
      return bar.totalEventCount > 0 ? '#FF1553' : '#FFE2E7'
    },
    selectDevice (value) {
      if (this.segmentDevice === value) return
      this.segmentDevice = value
      this.onSegmentChange()
    },
    selectReferrer (value) {
      this.segmentReferrer = value
      this.isReferrerOpen = false
      this.onSegmentChange()
    },
    toggleNew () {
      if (this.segmentNew && !this.segmentReturn) {
        this.segmentNew = false
        this.segmentReturn = true
      } else {
        this.segmentNew = !this.segmentNew
      }
      this.onSegmentChange()
    },
    toggleReturn () {
      if (this.segmentReturn && !this.segmentNew) {
        this.segmentReturn = false
        this.segmentNew = true
      } else {
        this.segmentReturn = !this.segmentReturn
      }
      this.onSegmentChange()
    },
    onSegmentChange () {
      this.resetPlaybackState()
      this.$emit('segment-change', {
        device: this.segmentDevice,
        referrer: this.segmentReferrer,
        isNew: this.segmentNew,
        isReturn: this.segmentReturn
      })
      this.isSegmentOpen = false
    }
  }
}
</script>

<style scoped lang="scss">
.time-machine-control {
  background-color: #fff;
  border-radius: 12px 12px 0 0;
  padding: 8px 16px;
  box-sizing: border-box;
  min-height: 118px;
  position: relative;

  &.disabled {
    pointer-events: none;

    .time-machine-control__header__left__item {
      .date {
        color: #BBB;
      }

      .time {
        color: #CCC;

        .icon {
          opacity: 0.3;
        }
      }

      .play-time {
        background: #CCC;
        color: #FFF;
      }

      .segment-filter {
        border-color: #EBEBEB;

        &__text {
          color: #C7C7C7;
        }

        .icon {
          opacity: 0.3;
        }
      }
    }

    .time-machine-control__header__right {
      .label {
        background-color: #CCC;
      }

      .label-text {
        color: #BBB;
      }
    }

    .time-machine-control__content__left {
      &__speed {
        border-color: #EBEBEB;

        .speed-value {
          color: #C7C7C7;
        }

        .icon {
          opacity: 0.3;
        }
      }

      &__play .play-btn {
        border-color: #EBEBEB;
        color: #C7C7C7;

        .icon {
          opacity: 0.3;
        }
      }
    }

    .time-machine-control__content__timeline {
      &__chart:after {
        background-color: rgba(0, 0, 0, 0.06);
      }

      .playhead .icon {
        opacity: 0.3;
      }

      &__nav {
        border-color: #CCC;
        background: #F5F5F5;

        .icon {
          opacity: 0.3;
        }
      }
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__left {
      display: flex;
      align-items: center;
      gap: 8px;

      &__item {
        display: flex;
        align-items: center;
        gap: 4px;

        &:not(:first-child) {
          padding-left: 8px;
        }

        &:not(:last-child) {
          position: relative;

          &::before {
            content: '';
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: -8px;
            width: 1px;
            height: 9px;
            background-color: #ccc;
          }
        }

        .date {
          color: #444;
          font-size: 15px;
          font-weight: 600;
          line-height: 100%;
          letter-spacing: -0.225px;
        }

        .time {
          display: flex;
          align-items: center;
          gap: 2px;
          color: #EC0047;
          font-size: 13px;
          font-weight: 500;
          line-height: 100%;
        }

        .play-time {
          display: flex;
          min-width: 40px;
          padding: 2px 4px;
          justify-content: center;
          align-items: center;
          gap: 10px;
          border-radius: 4px;
          background: #444;
          color: #FFF;
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          line-height: 100%;
          box-sizing: border-box;
          font-variant-numeric: tabular-nums;
        }

        .segment-filter {
          display: flex;
          align-items: center;
          gap: 4px;
          border-radius: 5px;
          border: 1px solid #E0E0E0;
          background: #FFF;
          padding: 6px;
          box-sizing: border-box;
          transition: all 0.3s ease;
          cursor: pointer;

          &__text {
            color: #444;
            font-size: 12px;
            font-weight: 500;
            line-height: 100%;
          }

          &:hover {
            border-radius: 5px;
            border: 1px solid #E0E0E0;
            background: #F4F4F4;
          }
        }
      }
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-right: 4px;

      .label {
        width: 9px;
        height: 3px;
        background-color: #FF1553;
        border-radius: 2px;

        &-text {
          color: #444;
          font-size: 10px;
          font-weight: 400;
          line-height: 100%;
        }
      }
    }
  }

  &__content {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding-bottom: 24px;

    &__left {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;

      &__speed {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 3px;
        padding: 5.5px 6px;
        border-radius: 5px;
        border: 1px solid #E0E0E0;
        background: #FFF;
        cursor: pointer;
        transition: all 0.2s ease;
        box-sizing: border-box;
        min-width: 46px;
        max-width: 46px;
        max-height: 22px;

        &:hover {
          background: #F4F4F4;
        }

        .speed-value {
          color: #444;
          font-size: 11px;
          font-weight: 500;
          line-height: 100%;
        }

        .speed-dropdown {
          position: absolute;
          top: -1px;
          left: -1px;
          min-width: 100%;
          margin: 0;
          padding: 0;
          padding-inline-start: 0;
          list-style: none;
          border-radius: 5px;
          border: 1px solid #E0E0E0;
          background: #FFF;
          box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
          z-index: 10;
          transform-origin: top;
          transition: transform .2s ease, opacity .1s ease;

          li {
            padding: 6px;
            color: #444;
            font-size: 11px;
            font-weight: 600;
            line-height: 100%;
            text-align: center;
            cursor: pointer;

            &:first-child {
              border-radius: 5px 5px 0 0;
            }

            &:last-child {
              border-radius: 0 0 5px 5px;
            }

            &:hover {
              background: #F4F4F4;
            }

            &.active {
              background: #F4F4F4;

              &:first-child {
                border-radius: 4px 4px 0 0;
              }

              &:last-child {
                border-radius: 0 0 4px 4px;
              }
            }
          }
        }
      }

      &__play {
        .play-btn {
          display: flex;
          align-items: center;
          gap: 3px;
          padding: 5.5px 6px;
          border-radius: 5px;
          border: 1px solid #E0E0E0;
          background: #FFF;
          color: #444;
          font-size: 11px;
          font-weight: 500;
          line-height: 100%;
          cursor: pointer;
          transition: all 0.2s ease;
          box-sizing: border-box;
          min-width: 46px;
          max-width: 46px;
          max-height: 22px;

          &:hover {
            background: #F4F4F4;
          }

          &.playing {
            border-radius: 5px;
            background: linear-gradient(0deg, #888 0.01%, #666 99.85%);
            border: 1px solid transparent;
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.30) inset;
            color: #FFF;
          }
        }
      }
    }

    &__timeline {
      flex: 1;
      min-width: 0;
      position: relative;

      &__chart {
        position: relative;
        width: 100%;
        height: 53px;
        overflow-x: auto;
        overflow-y: hidden;
        cursor: pointer;
        scrollbar-width: none;

        &::-webkit-scrollbar {
          display: none;
        }

        &:after {
          content: '';
          position: absolute;
          top: 92%;
          left: 0;
          width: 100%;
          height: 1px;
          background-color: rgba(0, 0, 0, 0.12);
        }

        .bar-chart {
          display: block;
          flex-shrink: 0;
        }
      }

      .playhead {
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 3;
        cursor: grab;
        pointer-events: auto;
        transition: transform 0.15s ease;

        &:active,
        &.dragging {
          cursor: grabbing;
        }
      }

      &__nav {
        position: absolute;
        top: 54px;
        transform: translateX(-50%);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 2px;
        padding: 3.5px 4px;
        border-radius: 4px;
        box-sizing: border-box;
        border: 1px solid #AAA;
        background: #FFF;
        width: 24px;
        height: 11px;
        cursor: grab;
        transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;

        &:hover {
          transform: translateX(-50%) scale(1.1);
          background: #111;
          border-color: transparent;
        }

        &:active,
        &.dragging {
          cursor: grabbing;
          background: #0E90A5;
          border-color: #0E90A5;
        }

        .nav-icon--left {
          transform: rotate(180deg);
        }
      }
    }
  }

  &__segment {
    height: 100%;
    width: 100%;
    padding: 16px 12px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    box-sizing: border-box;

    &__header {
      .segment-title {
        color: #444;
        font-size: 16px;
        font-weight: 600;
        line-height: 100%;
      }

      .segment-close {
        position: absolute;
        top: 16px;
        right: 16px;
      }
    }

    &__body {
      display: flex;
      align-items: center;
      gap: 16px;

      .segment-device {
        display: flex;
        align-items: center;
        gap: 4px;

        .device-btn {
          width: 34px;
          height: 34px;
          border-radius: 17px;
          border: none;
          background: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.3s ease;

          &:hover:not(.on) {
            .icon--device-desktop {
              background-image: url(../../assets/img/heatmap/heatmap_device_desktop_hover.svg);
            }

            .icon--device-tablet {
              background-image: url(../../assets/img/heatmap/heatmap_device_tablet_hover.svg);
            }

            .icon--device-phone {
              background-image: url(../../assets/img/heatmap/heatmap_device_phone_hover.svg);
            }
          }

          &.on {
            background: #EC0047;
          }
        }

        .device-line {
          width: 1px;
          height: 10px;
          background-color: #E0E0E0;
        }
      }

      .segment-referrer {
        position: relative;
        margin-left: 7px;

        .referrer-select {
          position: relative;
          padding-left: 15px;
          display: inline-block;
          min-width: 284px;
          height: 38px;
          line-height: 36px;
          border-radius: 6px;
          border: solid 1px #bbbbbb;
          background-color: #ffffff;
          box-sizing: border-box;
          font-size: 13px;
          color: #2f2f2f;
          cursor: pointer;

          &__text {
            display: inline-block;
            max-width: 220px;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            vertical-align: bottom;
            font-weight: 400;
            color: #EC0047;
          }

          .icon {
            pointer-events: none;
            position: absolute;
            top: 50%;
            right: 12px;
            transform: translateY(-50%);
          }
        }

        .referrer-dropdown {
          overflow: hidden;
          position: absolute;
          top: -1px;
          left: -1px;
          min-width: 100%;
          margin: 0;
          padding: 0;
          padding-inline-start: 0;
          list-style: none;
          background-color: #fff;
          z-index: 10;
          border-radius: 6px;
          box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08);
          box-sizing: border-box;
          max-height: 320px;
          border: 1px solid rgba(0, 0, 0, 0.25);
          transform-origin: top;
          transition: transform .2s ease, opacity .1s ease;

          overflow-y: auto;

          li {
            display: flex;
            align-items: center;
            padding: 0 15px;
            font-weight: 500;
            height: 38px;
            line-height: 38px;
            color: #444;
            cursor: pointer;
            box-sizing: border-box;

            &:first-child {
              border-radius: 6px 6px 0 0;
            }

            &:last-child {
              border-radius: 0 0 6px 6px;
            }

            &:hover {
              background-color: #ebebeb;
            }

            &.on {
              color: #EC0047;
              background-color: #f4f4f4;
            }

            .referrer-name {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              max-width: 200px;
            }

            .referrer-percent {
              flex-shrink: 0;
              margin-left: 6px;
            }
          }
        }
      }

      .segment-visitor {
        display: flex;
        align-items: center;
        gap: 16px;

        .checkbox_wrap {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;

          .icon {
            flex-shrink: 0;
          }

          .text {
            color: #2F2F2F;
            font-size: 13px;
            font-weight: 400;
            line-height: 100%;
          }
        }
      }
    }
  }
}

.slide-enter,
.slide-leave-to {
  transform: scaleY(0);
  opacity: 0;
}
</style>
